/**
 * Reads a File (CSV / TXT / XLSX / XLS) and returns raw CSV text.
 *
 * - For .csv / .txt: returns the file's text as-is.
 * - For .xlsx / .xls: parses the first worksheet with SheetJS and converts
 *   it to a CSV string, so downstream parsers keep working unchanged.
 *
 * Throws if the file is empty, too large, or unreadable.
 */
import * as XLSX from "xlsx";

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

export async function fileToCsv(file) {
  if (!file) throw new Error("No file selected");
  if (file.size > MAX_BYTES)
    throw new Error("File too big — keep it under 10MB.");

  const name = (file.name || "").toLowerCase();
  const isExcel = name.endsWith(".xlsx") || name.endsWith(".xls");

  if (!isExcel) {
    // Plain text / CSV
    return await file.text();
  }

  // Excel path
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const firstSheet = wb.SheetNames[0];
  if (!firstSheet) throw new Error("Excel file has no sheets");
  const ws = wb.Sheets[firstSheet];
  // sheet_to_csv preserves header row + all data rows as CSV
  const csv = XLSX.utils.sheet_to_csv(ws, { blankrows: false });
  return csv;
}

export const FILE_ACCEPT =
  ".csv,.txt,.xlsx,.xls,text/csv,text/plain," +
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "application/vnd.ms-excel";
